import React, { useEffect, useState } from 'react';
import RichText from '@/components/RichText';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import MemberAvatar from '@/components/MemberAvatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import MeetingRequestDialog from '@/components/MeetingRequestDialog';
import {
  MapPin, Tag, Briefcase, Mail, Phone, Linkedin, Building2, Handshake,
  UserPlus, UserCheck, MessageCircle, Share2, Check, ChevronDown, ChevronUp,
  ExternalLink, FlaskConical, Sparkles, Flag, Target, Lightbulb, Users, X,
  Calendar, GraduationCap, CalendarPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { STAKEHOLDER_COLORS } from '@/components/profile/profileConstants';

const BACKEND_URL = '';

/**
 * MemberProfileModal — rich, in-place member profile modal.
 *
 * Props:
 *   member: { user_id, name, picture, ... } — minimum required to fetch the rich card
 *   open: boolean
 *   onClose: (open: boolean) => void
 *   user: current logged-in user (optional, hides Follow/Message on self)
 *   onFollow: (user_id) => void  (optional)
 *   isFollowing: boolean (optional)
 */
export default function MemberProfileModal({ member, open, onClose, user, onFollow, isFollowing, eventContext, onMeetingRequested }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [enriched, setEnriched] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [internalFollowing, setInternalFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  // Public event & course history — surfaced as a "Track record" section
  // so members can see what events/courses someone has been part of.
  const [history, setHistory] = useState(null);
  const shareUrl = member ? `${window.location.origin}/profile/public/${member.user_id}` : '';
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('session_token') : null;

  useEffect(() => {
    if (!member || !open) { setEnriched(null); setShowMore(false); setHistory(null); return; }
    fetch(`${BACKEND_URL}/api/members/${member.user_id}/profile-card`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setEnriched(data); })
      .catch(() => {});
    // Pull the public event/course history in parallel with the main
    // profile card so the section paints almost immediately.
    fetch(`${BACKEND_URL}/api/users/${member.user_id}/event-history`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setHistory(data); })
      .catch(() => {});
    // If the parent did not manage follow state, look it up ourselves once.
    if (!onFollow && member.user_id !== user?.user_id) {
      fetch(`${BACKEND_URL}/api/members/me/following`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.ok ? r.json() : [])
        .then(ids => { if (Array.isArray(ids)) setInternalFollowing(ids.includes(member.user_id)); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.user_id, open]);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Profile link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFollowInternal = async (targetId) => {
    if (onFollow) { onFollow(targetId); return; }
    if (followBusy) return;
    setFollowBusy(true);
    const wasFollowing = internalFollowing;
    try {
      const res = await fetch(`${BACKEND_URL}/api/members/${targetId}/follow`, {
        method: wasFollowing ? 'DELETE' : 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        setInternalFollowing(!wasFollowing);
        toast.success(wasFollowing ? 'Unfollowed' : 'Following — they\'ll get a notification');
      } else {
        toast.error('Could not update follow status');
      }
    } catch {
      toast.error('Could not update follow status');
    } finally {
      setFollowBusy(false);
    }
  };

  if (!member) return null;
  const data = enriched || member;
  const p = data.profile || {};
  const contact = data.contact || {};
  const isSelf = data.user_id === user?.user_id;
  const showFollowMessage = !isSelf;
  const followingNow = onFollow ? isFollowing : internalFollowing;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-0 [&>button.opacity-70]:hidden" data-testid="member-profile-modal">
        <DialogHeader>
          <DialogTitle className="sr-only">Member Profile</DialogTitle>
        </DialogHeader>

        {/* Banner action bar: Follow + Close, both visible inside the banner */}
        <div className="absolute top-3 right-3 z-30 flex items-center gap-2" data-testid="modal-banner-actions">
          {showFollowMessage && (
            <button
              type="button"
              onClick={() => handleFollowInternal(data.user_id)}
              disabled={followBusy}
              aria-label={followingNow ? 'Unfollow member' : 'Follow member'}
              data-testid="modal-follow-btn"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-md ring-1 transition-colors focus:outline-none focus:ring-2 focus:ring-white/60 disabled:opacity-60 ${
                followingNow
                  ? 'bg-white text-brand-navy ring-white hover:bg-slate-100'
                  : 'bg-brand-navy text-white ring-white/40 hover:bg-brand-dark'
              }`}
            >
              {followingNow ? <UserCheck className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
              {followingNow ? 'Following' : 'Follow'}
            </button>
          )}
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close profile"
            data-testid="modal-close-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-slate-700 text-xs font-semibold shadow-md ring-1 ring-slate-200 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-navy/50 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Close
          </button>
        </div>

        {/* Header banner with stats */}
        <div className="relative h-28 -mt-px overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/80 to-brand-sage" />
          {data.header_image && (
            <img src={data.header_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90"
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <div className="absolute top-12 right-4 flex flex-wrap gap-1.5 justify-end max-w-[60%]" data-testid="modal-stats-pills">
            {(enriched?.total_posts || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/20" data-testid="stat-posts">
                <MessageCircle className="h-3 w-3" /> {enriched.total_posts} {enriched.total_posts === 1 ? 'post' : 'posts'}
              </span>
            )}
            {(enriched?.britelab_contributions?.length || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/20" data-testid="stat-labs">
                <FlaskConical className="h-3 w-3" /> {enriched.britelab_contributions.length} {enriched.britelab_contributions.length === 1 ? 'lab' : 'labs'}
              </span>
            )}
            {(enriched?.total_events || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/20" data-testid="stat-events">
                <Flag className="h-3 w-3" /> {enriched.total_events} {enriched.total_events === 1 ? 'event' : 'events'}
              </span>
            )}
            {(enriched?.completed_courses?.length || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/20" data-testid="stat-courses">
                <Sparkles className="h-3 w-3" /> {enriched.completed_courses.length} {enriched.completed_courses.length === 1 ? 'course' : 'courses'}
              </span>
            )}
            {(enriched?.total_followers || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/20" data-testid="stat-followers">
                <UserCheck className="h-3 w-3" /> {enriched.total_followers} {enriched.total_followers === 1 ? 'follower' : 'followers'}
              </span>
            )}
          </div>
        </div>

        <div className="px-5 sm:px-6 pb-5 pt-3 relative">
          <div className="flex items-start gap-4 mb-4">
            <MemberAvatar
              name={data.name}
              picture={data.picture}
              size={128}
              className="relative z-10 border-4 border-white shadow-lg -mt-20 bg-white"
            />
            <div className="flex-1 min-w-0 pt-3">
              <h2 className="text-2xl font-bold font-heading text-slate-800 leading-tight" data-testid="modal-member-name">{data.name}</h2>
              {p.role && <p className="text-brand-sage font-semibold text-base" data-testid="modal-job-title">{p.role}</p>}
              {p.organisation && !data.company_name && (
                <p className="text-sm text-slate-500" data-testid="modal-organisation">{p.organisation}</p>
              )}
            </div>
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-3">
            {p.location && (
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}{p.country ? `, ${p.country}` : ''}</span>
            )}
            {p.sector && (
              <span className="inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{p.sector}</span>
            )}
            {p.years_experience && (
              <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{p.years_experience}</span>
            )}
            {data.created_at && (
              <span className="inline-flex items-center gap-1.5">Member since {new Date(data.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>
            )}
          </div>

          {/* Identity badges */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {p.background && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STAKEHOLDER_COLORS[p.background] || 'bg-slate-100 text-slate-600'}`} data-testid="modal-stakeholder-type">
                {p.background}
              </span>
            )}
            {(Array.isArray(p.self_descriptions) && p.self_descriptions.length > 0
              ? p.self_descriptions
              : (p.self_description ? [p.self_description] : [])
            ).map((d, i) => (
              <span key={d || i} className="text-xs font-medium px-2.5 py-1 rounded-full bg-brand-navy/5 text-brand-navy border border-brand-navy/15" data-testid={`modal-self-description-${i}`}>
                {d}
              </span>
            ))}
            {data.company_id && data.company_name && (
              <button onClick={() => { onClose(false); navigate(`/companies/${data.company_id}`); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
                data-testid="modal-company-pill">
                <Building2 className="h-3 w-3" /> {data.company_name}
              </button>
            )}
            {data.company_id && data.company_is_partner && (
              <button onClick={() => { onClose(false); navigate(`/partners/partner_from_${data.company_id}`); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 transition-colors"
                data-testid="modal-partner-pill">
                <Handshake className="h-3 w-3" /> Partner Profile
              </button>
            )}
          </div>

          {/* About */}
          {p.bio && (
            <div className="mb-4 p-4 rounded-lg bg-slate-50/70 border border-slate-100" data-testid="modal-bio">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Users className="h-3 w-3" /> About
              </p>
              <RichText html={p.bio} className="text-sm text-slate-700 leading-relaxed" />
            </div>
          )}

          {/* Track record — events attending / attended + courses completed.
              Hidden when the member has nothing on their record yet so we
              never show an empty "events" block on brand-new accounts. */}
          {history && (history.attending?.length > 0 || history.attended?.length > 0 || history.courses_completed?.length > 0) && (
            <div className="mb-4 p-4 rounded-lg bg-amber-50/40 border border-amber-100" data-testid="modal-event-history">
              <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> Track Record
              </p>
              {history.attending?.length > 0 && (
                <div className="mb-2">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Attending</p>
                  <div className="flex flex-wrap gap-1.5">
                    {history.attending.map(ev => (
                      <span key={ev.event_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
                        data-testid={`track-attending-${ev.event_id}`}
                        title={`${ev.title}${ev.date ? ` · ${new Date(ev.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}`}>
                        <Calendar className="h-2.5 w-2.5" /> {ev.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {history.attended?.length > 0 && (
                <div className="mb-2">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Attended</p>
                  <div className="flex flex-wrap gap-1.5">
                    {history.attended.slice(0, 8).map(ev => (
                      <span key={ev.event_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white text-slate-700 border border-slate-200"
                        data-testid={`track-attended-${ev.event_id}`}
                        title={`${ev.title}${ev.date ? ` · ${new Date(ev.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}`}>
                        <Check className="h-2.5 w-2.5 text-emerald-600" /> {ev.title}
                      </span>
                    ))}
                    {history.attended.length > 8 && (
                      <span className="text-[10px] text-slate-400 self-center">+{history.attended.length - 8} more</span>
                    )}
                  </div>
                </div>
              )}
              {history.courses_completed?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Courses completed</p>
                  <div className="flex flex-wrap gap-1.5">
                    {history.courses_completed.slice(0, 6).map(c => (
                      <span key={c.course_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand-blue/10 text-brand-navy border border-brand-blue/20"
                        data-testid={`track-course-${c.course_id}`}>
                        <GraduationCap className="h-2.5 w-2.5" /> {c.title}
                      </span>
                    ))}
                    {history.courses_completed.length > 6 && (
                      <span className="text-[10px] text-slate-400 self-center">+{history.courses_completed.length - 6} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Two-column: Experience + Networking */}
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            {(p.expertise?.length > 0 || p.qualifications?.length > 0 || p.certifications?.length > 0 || p.specialist_areas?.length > 0 || p.projects?.length > 0) && (
              <div className="p-4 rounded-lg border border-slate-100 bg-white" data-testid="modal-experience">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Briefcase className="h-3 w-3" /> Experience</p>
                <div className="space-y-3">
                  {p.expertise?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Expertise</p>
                      <div className="flex flex-wrap gap-1">{p.expertise.slice(0, 8).map(e => <Badge key={e} variant="outline" className="text-[10px] bg-brand-navy/5 text-brand-navy border-brand-navy/15">{e}</Badge>)}</div>
                    </div>
                  )}
                  {p.specialist_areas?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Specialist areas</p>
                      <div className="flex flex-wrap gap-1">{p.specialist_areas.slice(0, 6).map(s => <Badge key={s} variant="outline" className="text-[10px] bg-brand-sage/10 text-brand-sage border-brand-sage/30">{s}</Badge>)}</div>
                    </div>
                  )}
                  {p.qualifications?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Qualifications</p>
                      <div className="flex flex-wrap gap-1">{p.qualifications.slice(0, 6).map(q => <Badge key={q} variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-200">{q}</Badge>)}</div>
                    </div>
                  )}
                  {p.certifications?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Certifications</p>
                      <div className="flex flex-wrap gap-1">{p.certifications.slice(0, 5).map(c => <Badge key={c} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{c}</Badge>)}</div>
                    </div>
                  )}
                  {p.projects?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Projects</p>
                      <div className="flex flex-wrap gap-1">{p.projects.slice(0, 5).map(pr => <Badge key={pr} variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{pr}</Badge>)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(p.working_on || p.looking_for?.length > 0 || p.interests?.length > 0 || p.challenges?.length > 0 || p.priorities_2026?.length > 0 || p.community_goals?.length > 0) && (
              <div className="p-4 rounded-lg border border-slate-100 bg-white" data-testid="modal-networking">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Users className="h-3 w-3" /> Networking</p>
                <div className="space-y-3">
                  {p.working_on && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1">Currently working on</p>
                      <p className="text-xs text-slate-700 whitespace-pre-line line-clamp-3">{p.working_on}</p>
                    </div>
                  )}
                  {p.looking_for?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5 flex items-center gap-1"><Target className="h-3 w-3" /> Looking for</p>
                      <div className="flex flex-wrap gap-1">{p.looking_for.slice(0, 6).map(l => <Badge key={l} variant="outline" className="text-[10px] bg-brand-navy/5 text-brand-navy border-brand-navy/15">{l}</Badge>)}</div>
                    </div>
                  )}
                  {p.interests?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Interests</p>
                      <div className="flex flex-wrap gap-1">{p.interests.slice(0, 6).map(i => <Badge key={i} variant="outline" className="text-[10px] bg-brand-sage/10 text-brand-sage border-brand-sage/30">{i}</Badge>)}</div>
                    </div>
                  )}
                  {p.challenges?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5 flex items-center gap-1"><Lightbulb className="h-3 w-3" /> Challenges</p>
                      <div className="flex flex-wrap gap-1">{p.challenges.slice(0, 5).map(c => <Badge key={c} variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">{c}</Badge>)}</div>
                    </div>
                  )}
                  {p.priorities_2026?.length > 0 && (
                    <div><p className="text-[10px] text-slate-500 font-medium mb-1.5">Priorities for 2026</p>
                      <div className="flex flex-wrap gap-1">{p.priorities_2026.slice(0, 5).map(pr => <Badge key={pr} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{pr}</Badge>)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          {(enriched?.recent_posts?.length > 0 || enriched?.britelab_contributions?.length > 0 || enriched?.next_event || enriched?.completed_courses?.length > 0) && (
            <div className="mb-4 p-4 rounded-lg border border-slate-100 bg-white" data-testid="modal-recent-activity">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Recent activity</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {enriched?.recent_posts?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-slate-500 font-medium mb-1.5 flex items-center gap-1"><MessageCircle className="h-3 w-3" /> Latest forum posts</p>
                    <ul className="space-y-1">
                      {enriched.recent_posts.map(post => (
                        <li key={post.post_id} className="text-xs">
                          <button onClick={() => { onClose(false); navigate(`/forum?post=${post.post_id}`); }}
                            className="text-slate-700 hover:text-brand-navy hover:underline text-left">
                            {post.title || '(Untitled post)'}
                          </button>
                          <span className="text-[10px] text-slate-400 ml-1">· {post.like_count} likes · {post.comment_count} comments</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {enriched?.britelab_contributions?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-slate-500 font-medium mb-1.5 flex items-center gap-1"><FlaskConical className="h-3 w-3" /> Member of {enriched.britelab_contributions.length} BriteLab{enriched.britelab_contributions.length === 1 ? '' : 's'}</p>
                    <div className="flex flex-wrap gap-1">
                      {enriched.britelab_contributions.map(lab => (
                        <button key={lab.lab_id} onClick={() => { onClose(false); navigate(`/britelabs/${lab.lab_id}`); }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
                          data-testid={`modal-britelab-${lab.lab_id}`}>
                          {lab.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {enriched?.next_event && (
                  <div>
                    <p className="text-[10px] text-slate-500 font-medium mb-1.5 flex items-center gap-1"><Flag className="h-3 w-3" /> Next event</p>
                    <button onClick={() => { onClose(false); navigate('/events'); }}
                      className="text-xs text-slate-700 hover:text-brand-navy hover:underline text-left">
                      {enriched.next_event.title}
                      <span className="text-[10px] text-slate-400 ml-1">· {new Date(enriched.next_event.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                    </button>
                  </div>
                )}
                {enriched?.completed_courses?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-slate-500 font-medium mb-1.5">Completed {enriched.completed_courses.length} course{enriched.completed_courses.length === 1 ? '' : 's'}</p>
                    <ul className="space-y-0.5">
                      {enriched.completed_courses.slice(0, 3).map(c => (
                        <li key={c.course_id} className="text-xs text-slate-700">{c.title}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact info */}
          {(contact.email || contact.phone || contact.linkedin) && (
            <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid="modal-contact-info">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Contact</p>
              <div className="flex flex-wrap gap-3">
                {contact.email && (<a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-xs text-brand-navy hover:underline" data-testid="modal-contact-email"><Mail className="h-3.5 w-3.5" /> {contact.email}</a>)}
                {contact.phone && (<a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 text-xs text-brand-navy hover:underline" data-testid="modal-contact-phone"><Phone className="h-3.5 w-3.5" /> {contact.phone}</a>)}
                {contact.linkedin && (<a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-brand-navy hover:underline" data-testid="modal-contact-linkedin"><Linkedin className="h-3.5 w-3.5" /> LinkedIn</a>)}
              </div>
            </div>
          )}

          {/* Work style toggle */}
          {(p.behavioural_decisions || p.behavioural_processing || p.behavioural_working || p.behavioural_collaboration) && (
            <button onClick={() => setShowMore(!showMore)} className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-brand-navy hover:text-brand-dark transition-colors py-2 mb-2" data-testid="modal-toggle-more">
              {showMore ? <><ChevronUp className="h-3.5 w-3.5" /> Hide work style</> : <><ChevronDown className="h-3.5 w-3.5" /> Show work style</>}
            </button>
          )}

          {showMore && (p.behavioural_decisions || p.behavioural_processing || p.behavioural_working || p.behavioural_collaboration) && (
            <div className="mb-4 p-3 rounded-lg bg-indigo-50/50 border border-indigo-100 animate-in fade-in duration-200" data-testid="modal-extended-profile">
              <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wider mb-2">Personality &amp; Work Style</p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {p.behavioural_decisions && (<div><p className="text-[10px] text-indigo-400 font-medium">Decision Making</p><p className="text-slate-700">{p.behavioural_decisions}</p></div>)}
                {p.behavioural_processing && (<div><p className="text-[10px] text-indigo-400 font-medium">Processing Style</p><p className="text-slate-700">{p.behavioural_processing}</p></div>)}
                {p.behavioural_working && (<div><p className="text-[10px] text-indigo-400 font-medium">Working Style</p><p className="text-slate-700">{p.behavioural_working}</p></div>)}
                {p.behavioural_collaboration && (<div><p className="text-[10px] text-indigo-400 font-medium">Collaboration</p><p className="text-slate-700">{p.behavioural_collaboration}</p></div>)}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-3 border-t border-slate-100 space-y-2">
            {!isSelf && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button variant="outline" className="w-full min-w-0" data-testid="modal-message-btn"
                  onClick={() => {
                    onClose(false);
                    // When the modal was opened from an event directory, carry
                    // the event context through so the recipient's inbox opens
                    // straight into the event-scoped thread.
                    const q = eventContext?.event_id
                      ? `?to=${data.user_id}&event_id=${eventContext.event_id}`
                      : `?to=${data.user_id}`;
                    navigate(`/messages${q}`);
                  }}>
                  <MessageCircle className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  <span className="truncate">
                    {eventContext?.event_title
                      ? `Message me about ${eventContext.event_title.replace(/Sustainable Logistics LIVE/i, 'SLL')}`
                      : 'Message'}
                  </span>
                </Button>

                {/* Request-to-Meet — only rendered when the modal was opened
                    from an event directory AND the recipient hasn't opted
                    out via notification_preferences.meeting_requests. The
                    preference is surfaced on the enriched profile card. */}
                {eventContext?.event_id && (data.allows_meeting_requests !== false) && (
                  <Button
                    data-testid="modal-request-meet-btn"
                    onClick={() => setShowMeetingDialog(true)}
                    className="w-full bg-brand-sage hover:bg-brand-sage/90 text-white"
                  >
                    <CalendarPlus className="h-4 w-4 mr-1.5" /> Request to Meet
                  </Button>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 text-sm" data-testid="modal-share-btn" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4 mr-1.5 text-green-500" /> : <Share2 className="h-4 w-4 mr-1.5" />}
                {copied ? 'Copied!' : 'Share Profile'}
              </Button>
              <Button variant="outline" size="sm" asChild data-testid="modal-open-public">
                <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Open
                </a>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Meeting-request composition dialog — rendered as a sibling to the
          profile modal rather than inside it so the two can be visible
          together, with the profile summary providing context behind. */}
      <MeetingRequestDialog
        open={showMeetingDialog}
        onClose={() => setShowMeetingDialog(false)}
        event={eventContext}
        recipient={data}
        onCreated={(mr) => { onMeetingRequested?.(mr); }}
      />
    </Dialog>
  );
}
